import React, { useState, useEffect, useRef } from 'react';
import API, { MARKET_REFRESH_MS, resolveAssetUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import { Camera, Save, User, Phone, Mail, Trophy, TrendingUp } from 'lucide-react';

export default function ProfilePage() {
  const { updateUser } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast]       = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let firstLoad = true;
    const fetchProfile = () => {
      API.get('/profile').then(r => {
        const profileData = { ...r.data, avatar_url: resolveAssetUrl(r.data.avatar_url) };
        setProfile(profileData);
        if (firstLoad) {
          setDisplayName(profileData.display_name || profileData.username || '');
          setPhone(profileData.phone || '');
          firstLoad = false;
        }
      }).catch(console.error);
    };

    fetchProfile();
    const interval = setInterval(fetchProfile, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await API.put('/profile', { display_name: displayName, phone });
      setToast({ msg: 'Profile updated!', type: 'success' });
      const r = await API.get('/profile');
      const profileData = { ...r.data, avatar_url: resolveAssetUrl(r.data.avatar_url) };
      setProfile(profileData);
      updateUser({
        display_name: profileData.display_name,
        phone: profileData.phone,
        avatar_url: profileData.avatar_url,
      });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Failed to save', type: 'error' });
    } finally { setSaving(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setToast({ msg: 'Image must be under 2MB', type: 'error' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const r = await API.post('/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const avatarUrl = `${resolveAssetUrl(r.data.avatar_url)}?v=${Date.now()}`;
      setProfile(p => ({ ...p, avatar_url: avatarUrl }));
      updateUser({ avatar_url: avatarUrl });
      setToast({ msg: 'Avatar updated!', type: 'success' });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Upload failed', type: 'error' });
    } finally { setUploading(false); }
  };

  const initials = (name) => (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  if (!profile) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 500, margin: '0 auto' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
      </div>

      {/* Avatar section */}
      <div className="card" style={{ textAlign: 'center', marginBottom: '1rem', padding: '1.5rem' }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
          {profile.avatar_url && profile.avatar_url !== 'none' ? (
            <img
              src={profile.avatar_url}
              alt="avatar"
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--accent)' }}
            />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'rgba(245,158,11,0.15)', color: 'var(--accent)',
              border: '3px solid var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.5rem',
            }}>
              {initials(profile.display_name || profile.username)}
            </div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 26, height: 26, borderRadius: '50%', border: '2px solid var(--bg-primary)',
              background: 'var(--accent)', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Camera size={12} />
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleAvatarUpload} />
        </div>

        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700 }}>
          {profile.display_name || profile.username}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>@{profile.username}</div>

        {/* Stats */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: '1rem' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: 'var(--accent)' }}>
              {parseFloat(profile.points).toFixed(0)}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>pts</div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700 }}>
              {profile.trade_count}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>trades</div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: 'var(--green)' }}>
              {profile.trades_remaining}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>left</div>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="card">
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <User size={13} /> Display Name
          </label>
          <input
            className="form-input"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={profile.username}
            maxLength={50}
          />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Shown in leaderboard and chat. Max 50 characters.
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Mail size={13} /> Email
          </label>
          <input className="form-input" value={profile.email} disabled style={{ opacity: 0.5 }} />
        </div>

        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Phone size={13} /> Phone Number (optional)
          </label>
          <input
            className="form-input"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="10-digit mobile number"
            maxLength={10}
          />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Used for OTP login when enabled. Enter without +91 prefix.
          </div>
        </div>

        <button
          className="btn btn-buy"
          style={{ width: '100%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onClick={saveProfile}
          disabled={saving}
        >
          <Save size={14} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
